/**
 * useReportTemplates Hook
 *
 * Custom hook for fetching and managing report templates
 */

import { useState, useEffect, useCallback } from 'react'
import { TaskReportService } from '@/api/generated/services/TaskReportService'
import type { ReportTemplateDetailResponse } from '@/api/generated/models/ReportTemplateDetailResponse'

export interface UseReportTemplatesResult {
  templates: ReportTemplateDetailResponse[]
  isLoading: boolean
  error: string | null
  refetch: () => Promise<void>
}

/**
 * Hook to fetch all report templates
 * Note: Currently fetches all templates without filtering by business
 * Business-specific settings are handled via report_settings in BusinessResponse
 */
export function useReportTemplates(): UseReportTemplatesResult {
  const [templates, setTemplates] = useState<ReportTemplateDetailResponse[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchTemplates = useCallback(async () => {
    setIsLoading(true)
    setError(null)

    try {
      // Note: API currently requires businessId but we need all templates
      // Passing undefined to get all available templates
      const response = await TaskReportService.appsTaskViewsGetAllReportTemplates({})

      setTemplates(response || [])
    } catch (err) {
      console.error('Failed to fetch report templates:', err)
      setError('보고서 템플릿을 불러오는 데 실패했습니다.')
      setTemplates([])
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchTemplates()
  }, [fetchTemplates])

  return {
    templates,
    isLoading,
    error,
    refetch: fetchTemplates
  }
}
